import { DiagnosticFormData } from '../types/diagnostic';

export interface Vulnerability {
  title: string;
  description: string;
  recommendations: string[];
  severity: 'high' | 'medium' | 'low';
  category: 'protection' | 'surveillance' | 'deterrence' | 'mixed';
}

interface CategoryScores {
  physicalProtection: number;
  surveillance: number;
  deterrence: number;
}

/**
 * Génère les vulnérabilités détectées de manière contextuelle et intelligente
 * en exploitant tous les critères du Sprint 1
 */
export function detectVulnerabilities(
  formData: DiagnosticFormData,
  categoryScores: CategoryScores
): Vulnerability[] {
  const vulnerabilities: Vulnerability[] = [];
  
  // Détermination du contexte de risque
  const isHighRisk = formData.crimeData && formData.crimeData.tauxPour1000Logements > 6.0;
  const isMediumRisk = formData.crimeData && formData.crimeData.tauxPour1000Logements > 4.0;
  
  // ══════════════════════════════════════════════════════════════
  // VULNÉRABILITÉS CRITIQUES (HAUTE PRIORITÉ)
  // ══════════════════════════════════════════════════════════════
  
  // 1. Porte bois standard en zone à risque
  if (formData.doorMaterial === 'standard_wood' && (isHighRisk || isMediumRisk)) {
    vulnerabilities.push({
      title: 'Point d\'accès principal vulnérable en zone à risque',
      description: `Votre porte d'entrée en bois standard constitue une vulnérabilité critique dans un secteur ${isHighRisk ? 'à risque élevé' : 'à risque modéré'} de cambriolage. Les portes en bois non renforcées cèdent rapidement face aux techniques d'effraction courantes.`,
      recommendations: [
        'Installer une porte blindée certifiée A2P BP3 (résistance 15 minutes minimum)',
        'Équiper immédiatement d\'une serrure multipoint 5 ou 7 points certifiée A2P',
        'Renforcer les gonds avec protection anti-dégondage',
        'Ajouter un judas grand angle et un entrebâilleur de sécurité'
      ],
      severity: 'high',
      category: 'protection'
    });
  }
  
  // 2. Simple vitrage au RDC ou étage bas
  if (formData.windowType === 'single_glazing' && formData.floors <= 1) {
    vulnerabilities.push({
      title: 'Vitrage extrêmement vulnérable aux effractions',
      description: 'Vos fenêtres en simple vitrage au rez-de-chaussée représentent une faille majeure de sécurité. Un simple vitrage se brise silencieusement en quelques secondes, offrant un accès immédiat aux cambrioleurs.',
      recommendations: [
        'Installation urgente de film anti-effraction retardateur P2A/P4A sur tous les vitrages accessibles',
        'Remplacement progressif par du double vitrage feuilleté 44.2 minimum',
        'Pose de détecteurs de bris de vitre acoustiques sur chaque fenêtre',
        'Installation de barreaux amovibles ou de grilles de protection si budget limité'
      ],
      severity: 'high',
      category: 'protection'
    });
  }
  
  // 3. Résidence secondaire sans télésurveillance en zone à risque
  if (formData.occupationType === 'secondary_residence' && 
      !formData.equipment.includes('remoteSurveillance') && 
      (isHighRisk || isMediumRisk)) {
    vulnerabilities.push({
      title: 'Résidence secondaire non surveillée en zone sensible',
      description: 'Votre logement est une résidence secondaire dans un secteur exposé aux cambriolages. L\'absence de surveillance à distance pendant vos absences prolongées en fait une cible privilégiée pour les cambrioleurs qui repèrent les habitations inoccupées.',
      recommendations: [
        'Souscrire immédiatement à un service de télésurveillance 24/7 avec intervention',
        'Installer des caméras connectées avec notifications push instantanées',
        'Équiper d\'un système d\'alarme avec transmission GSM et alerte automatique',
        'Mettre en place un programmateur d\'éclairage avec plages horaires variables',
        'Demander à un voisin ou gardien de vérifier régulièrement le logement'
      ],
      severity: 'high',
      category: 'surveillance'
    });
  }
  
  // 4. Famille avec enfants au RDC sans protection fenêtres
  if (formData.householdComposition === 'family' && 
      formData.floors <= 1 && 
      !formData.equipment.includes('bars') && 
      !formData.equipment.includes('windowLocks')) {
    vulnerabilities.push({
      title: 'Sécurité familiale compromise - Fenêtres non protégées',
      description: 'Avec des enfants au rez-de-chaussée, l\'absence de protection sur les fenêtres représente un double risque : intrusion facilitée et sécurité des enfants non assurée. Les fenêtres accessibles sont les points d\'entrée privilégiés des cambrioleurs.',
      recommendations: [
        'Installer des barreaux de sécurité ou grilles fixes sur toutes les fenêtres accessibles',
        'Équiper chaque fenêtre de serrures à clé avec blocage de l\'ouverture',
        'Poser des détecteurs de bris de vitre avec alarme sonore',
        'Ajouter un film de sécurité sur les vitrages pour retarder le bris'
      ],
      severity: 'high',
      category: 'protection'
    });
  }
  
  // 5. Accès dissimulé non sécurisé
  if (formData.sideAccess === 'hidden_access' && 
      !formData.equipment.includes('securityGate') && 
      categoryScores.surveillance < 50) {
    vulnerabilities.push({
      title: 'Accès dissimulé non surveillé ni sécurisé',
      description: 'Votre logement dispose d\'un accès latéral ou arrière peu visible depuis la rue, créant une zone de travail discrète pour les cambrioleurs. L\'absence de portail et de surveillance en fait un point d\'entrée privilégié totalement hors de vue.',
      recommendations: [
        'Installer un portail ou grille sécurisée avec serrure multipoint sur l\'accès dissimulé',
        'Placer des caméras de surveillance orientées vers tous les accès non visibles',
        'Mettre en place un éclairage automatique puissant couvrant tout le périmètre',
        'Installer des détecteurs de mouvement périmètriques avec alerte sonore',
        'Ajouter une alarme spécifique sur les portes et fenêtres donnant sur cet accès'
      ],
      severity: 'high',
      category: 'mixed'
    });
  }
  
  // 6. Pas de volets + zone peu éclairée + mauvaise surveillance
  if (formData.hasShutters === 'none' && 
      formData.streetLighting === 'poor' && 
      categoryScores.surveillance < 40) {
    vulnerabilities.push({
      title: 'Zone d\'ombre favorable aux intrusions discrètes',
      description: 'La combinaison de l\'absence de volets, d\'un éclairage public insuffisant et d\'un système de surveillance limité crée des conditions idéales pour des tentatives d\'effraction discrètes. Les cambrioleurs peuvent opérer sans être vus ni détectés.',
      recommendations: [
        'Installer en priorité un système d\'alarme avec sirène extérieure puissante',
        'Mettre en place un éclairage extérieur automatique avec détecteur de mouvement infrarouge',
        'Équiper de caméras de surveillance avec vision nocturne et projecteurs LED intégrés',
        'Ajouter des détecteurs de mouvement périmètriques sur tous les côtés',
        'Installer un film anti-effraction sur les vitrages comme mesure compensatoire'
      ],
      severity: 'high',
      category: 'mixed'
    });
  }
  
  // ══════════════════════════════════════════════════════════════
  // VULNÉRABILITÉS IMPORTANTES (PRIORITÉ MOYENNE)
  // ══════════════════════════════════════════════════════════════
  
  // 7. Absences prolongées sans simulateur de présence
  if ((formData.presenceRhythm === 'frequent_absences' || formData.presenceRhythm === 'occasional') &&
      !formData.equipment.includes('lightTimer') &&
      categoryScores.deterrence < 60) {
    const isSecondary = formData.presenceRhythm === 'occasional';
    vulnerabilities.push({
      title: isSecondary ? 'Résidence secondaire sans simulation de présence' : 'Absences prolongées détectables par observation',
      description: isSecondary
        ? 'Votre résidence occupée moins de 50% du temps est une cible privilégiée. L\'absence de simulation de présence signale clairement une inoccupation prolongée, attirant l\'attention des cambrioleurs.'
        : 'Vos absences régulières et prolongées peuvent être facilement repérées par des individus malveillants qui surveillent les habitudes du quartier. L\'absence de simulation de présence signale clairement que le logement est inoccupé.',
      recommendations: isSecondary ? [
        'Télésurveillance professionnelle 24/7 indispensable pour ce type de bien',
        'Programmateur d\'éclairage intelligent avec scénarios aléatoires',
        'Volets roulants automatisés avec ouverture/fermeture variable',
        'Caméras connectées avec notifications immédiates et enregistrement cloud',
        'Service de voisinage vigilant ou gardiennage durant vos absences'
      ] : [
        'Installer un programmateur d\'éclairage intelligent avec plages horaires variables',
        'Équiper de prises connectées pour simuler une activité dans différentes pièces',
        'Mettre en place un système d\'ouverture/fermeture automatique des volets',
        'Demander à un voisin de relever le courrier et varier le stationnement devant le logement',
        'Installer une sonnette vidéo pour répondre aux visiteurs à distance'
      ],
      severity: isSecondary ? 'high' : 'medium',
      category: 'deterrence'
    });
  }
  
  // 8. Jardin accessible sans portail
  if (formData.hasAccessibleGarden && !formData.equipment.includes('securityGate')) {
    vulnerabilities.push({
      title: 'Périmètre extérieur non sécurisé',
      description: 'Votre jardin est accessible depuis la rue sans obstacle significatif, offrant un accès direct et discret aux cambrioleurs. Une fois dans le jardin, ils peuvent travailler à l\'abri des regards pour forcer les accès arrière ou latéraux.',
      recommendations: [
        'Installer un portail ou une grille de sécurité avec serrure renforcée à l\'entrée du jardin',
        'Mettre en place un éclairage de périmètre automatique avec détection de mouvement',
        'Placer des caméras orientées vers les accès extérieurs et le jardin',
        'Ajouter des détecteurs de mouvement périmètriques en extérieur',
        'Renforcer la clôture si elle est basse (minimum 1,80m recommandé)'
      ],
      severity: 'medium',
      category: 'protection'
    });
  }
  
  // 9. Sous-sol avec protection faible
  if (formData.hasBasement && categoryScores.physicalProtection < 50 && 
      !formData.equipment.includes('doorSensor')) {
    vulnerabilities.push({
      title: 'Dépendances souterraines négligées dans la sécurisation',
      description: 'Votre sous-sol ou cave constitue un point d\'accès potentiel souvent oublié dans les dispositifs de sécurité. Les accès au sous-sol sont fréquemment moins renforcés que l\'entrée principale, ce qui en fait une cible pour les effractions.',
      recommendations: [
        'Renforcer la porte d\'accès au sous-sol avec une serrure multipoint',
        'Installer des détecteurs d\'ouverture sur toutes les portes menant au sous-sol',
        'Ajouter des détecteurs de mouvement dans les zones de circulation',
        'Éviter de stocker des objets de valeur en sous-sol',
        'S\'assurer que les soupiraux sont équipés de barreaux ou grilles scellées'
      ],
      severity: 'medium',
      category: 'protection'
    });
  }
  
  // 10. Absences régulières sans dissuasion visible
  if ((formData.presenceRhythm === 'empty_daytime' || formData.presenceRhythm === 'mostly_occupied') &&
      categoryScores.deterrence < 50 &&
      !formData.equipment.includes('securityStickers')) {
    vulnerabilities.push({
      title: 'Absences régulières repérables',
      description: 'Vos absences en journée (heures de travail) sont prévisibles et permettent aux cambrioleurs de planifier leurs actions. L\'absence d\'éléments dissuasifs visibles ne les incite pas à éviter votre logement.',
      recommendations: [
        'Installer des panneaux et autocollants "Alarme" et "Vidéosurveillance" bien visibles',
        'Mettre en place un programmateur d\'éclairage avec variabilité',
        'Installer une alarme connectée avec notifications sur smartphone',
        'Considérer des caméras factices visibles en complément',
        'Coordination avec les voisins pour surveillance mutuelle'
      ],
      severity: 'medium',
      category: 'deterrence'
    });
  }
  
  // 11. Visibilité faible + manque de surveillance
  if (formData.streetVisibility === 'low' && categoryScores.surveillance < 50) {
    vulnerabilities.push({
      title: 'Logement en retrait nécessitant surveillance compensatoire',
      description: 'Votre logement peu visible depuis la rue bénéficie d\'une surveillance naturelle limitée par les passants et voisins. Cette discrétion qui peut sembler agréable offre également aux cambrioleurs une zone de travail à l\'abri des regards indiscrets.',
      recommendations: [
        'Installer un système de caméras de surveillance couvrant tous les angles',
        'Équiper d\'un système d\'alarme avec sirène extérieure puissante et visible',
        'Mettre en place des détecteurs de mouvement périmètriques',
        'Installer un éclairage extérieur automatique pour compenser la faible exposition',
        'Envisager une télésurveillance professionnelle pour une réponse rapide'
      ],
      severity: 'medium',
      category: 'surveillance'
    });
  }
  
  // 12. Quartier isolé sans alarme ni télésurveillance
  if (formData.neighborhoodType === 'isolated' && 
      !formData.equipment.includes('alarm') && 
      !formData.equipment.includes('remoteSurveillance')) {
    vulnerabilities.push({
      title: 'Isolement géographique sans surveillance compensatoire',
      description: 'L\'isolement de votre quartier ralentit les interventions des forces de l\'ordre et élimine la surveillance naturelle du voisinage. Sans système d\'alerte automatique, vous êtes particulièrement vulnérable aux intrusions prolongées.',
      recommendations: [
        'Installer en priorité un système d\'alarme avec transmission automatique',
        'Souscrire à un service de télésurveillance avec intervention rapide',
        'Équiper de caméras connectées avec notifications push instantanées',
        'Mettre en place une liaison directe avec un centre de télésurveillance',
        'Créer un réseau de voisins vigilants avec système d\'alerte mutuelle'
      ],
      severity: 'medium',
      category: 'surveillance'
    });
  }
  
  // ══════════════════════════════════════════════════════════════
  // VULNÉRABILITÉS PAR SCORES DE CATÉGORIES (FALLBACK)
  // ══════════════════════════════════════════════════════════════
  
  // Si pas assez de vulnérabilités spécifiques détectées, ajouter les génériques
  if (vulnerabilities.length < 2) {
    // Protection physique insuffisante
    if (categoryScores.physicalProtection < 50) {
      vulnerabilities.push({
        title: 'Protection physique globalement insuffisante',
        description: 'L\'ensemble de vos dispositifs de protection physique présente des lacunes importantes. Les points d\'accès de votre logement ne sont pas suffisamment renforcés pour résister aux tentatives d\'effraction.',
        recommendations: [
          'Installer une porte blindée ou renforcer la porte existante',
          'Équiper toutes les portes de serrures multipoint certifiées A2P',
          'Renforcer les fenêtres accessibles avec barreaux ou vitrage sécurisé',
          'Ajouter un film anti-effraction sur les vitrages simples',
          'Sécuriser les accès secondaires (garage, cave, dépendances)'
        ],
        severity: categoryScores.physicalProtection < 35 ? 'high' : 'medium',
        category: 'protection'
      });
    }
    
    // Système de surveillance limité
    if (categoryScores.surveillance < 50) {
      vulnerabilities.push({
        title: 'Système de surveillance limité',
        description: 'Votre logement manque de dispositifs de détection d\'intrusion. Cette absence limite votre capacité à réagir rapidement en cas d\'effraction et à dissuader les cambrioleurs par la menace d\'une détection immédiate.',
        recommendations: [
          'Installer un système d\'alarme connecté avec télésurveillance',
          'Ajouter des caméras de surveillance (intérieures et extérieures)',
          'Équiper les ouvertures de détecteurs d\'ouverture magnétiques',
          'Installer des détecteurs de mouvement dans les zones stratégiques',
          'Mettre en place une sonnette vidéo connectée'
        ],
        severity: categoryScores.surveillance < 35 ? 'high' : 'medium',
        category: 'surveillance'
      });
    }
    
    // Dissuasion insuffisante
    if (categoryScores.deterrence < 50) {
      vulnerabilities.push({
        title: 'Éléments de dissuasion insuffisants',
        description: 'Votre logement ne présente pas suffisamment d\'éléments visibles pour dissuader les cambrioleurs de tenter une intrusion. L\'absence de signes de sécurité apparents peut donner l\'impression d\'une cible facile.',
        recommendations: [
          'Installer un éclairage extérieur automatique avec détecteur de mouvement',
          'Ajouter des panneaux et autocollants de sécurité bien visibles',
          'Mettre en place un système de simulation de présence',
          'Installer des caméras visibles avec voyants LED actifs',
          'Ajouter une sirène extérieure bien visible sur la façade'
        ],
        severity: categoryScores.deterrence < 35 ? 'high' : 'medium',
        category: 'deterrence'
      });
    }
  }
  
  // Limiter à 4 vulnérabilités maximum pour éviter la surcharge
  // Prioriser : high > medium > low, puis par ordre d'ajout
  const sortedVulnerabilities = vulnerabilities.sort((a, b) => {
    const severityOrder = { high: 0, medium: 1, low: 2 };
    return severityOrder[a.severity] - severityOrder[b.severity];
  });
  
  return sortedVulnerabilities.slice(0, 4);
}